import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plug, CheckCircle2, ExternalLink, Wallet, Repeat, Eye, MessageSquare } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { useModules } from "@/context/ModulesContext";

const freqLabels = { one_time: "One-time", weekly: "Weekly", biweekly: "Bi-weekly", monthly: "Monthly" };

const MODULE_GROUPS = [
  { label: "CRM Sidebar Pages", keys: [
    ["nav.dashboard", "Dashboard"], ["nav.estimates", "Estimates"], ["nav.quotes", "Quote Builder"], ["nav.invoices", "Invoices"],
    ["nav.leads", "Leads"], ["nav.catalog", "Catalog"], ["nav.schedule", "Schedule"], ["nav.dispatch", "Dispatch"],
    ["nav.quality-checks", "Quality Checks"], ["nav.cad", "Crew CAD"], ["nav.customers", "Customers"], ["nav.inventory", "Inventory"],
    ["nav.expenses", "Expenses"], ["nav.mileage", "Mileage"], ["nav.payroll", "Payroll"], ["nav.capacity", "Capacity & Mode"], ["nav.equipment", "Equipment"], ["nav.team", "Team"],
  ] },
  { label: "Dashboard Widgets", keys: [
    ["dash.stat_revenue", "Revenue stat"], ["dash.stat_outstanding", "Outstanding stat"], ["dash.stat_jobs_week", "Jobs This Week stat"], ["dash.stat_customers", "Active Customers stat"],
    ["dash.revenue_trend", "Revenue Trend chart"], ["dash.lead_pipeline", "Leads by Stage"], ["dash.upcoming_jobs", "Next Jobs list"],
  ] },
  { label: "Crew CAD Sections", keys: [
    ["cad.weather", "Weather card"], ["cad.announcements", "Announcements"], ["cad.crew", "On the crew"], ["cad.hours", "Today's Hours & Pay"], ["cad.quick_actions", "Quick actions (Supplies / Mileage / Issue)"],
  ] },
];

export default function Settings() {
  const [token, setToken] = useState("");
  const [businessId, setBusinessId] = useState("");
  const [status, setStatus] = useState({ connected: false, has_token: false, last_sync: "" });
  const [businesses, setBusinesses] = useState([]);
  const [testing, setTesting] = useState(false);
  const [freq, setFreq] = useState({ one_time: 1, weekly: 1, biweekly: 1, monthly: 1 });
  const [biz, setBiz] = useState({ name: "", address: "", phone: "", email: "", license: "", logo_url: "", terms: "" });
  const { refresh: refreshModules } = useModules();
  const [mods, setMods] = useState(null);
  const [msg, setMsg] = useState(null);
  const [testTo, setTestTo] = useState("");
  const [testing2, setTesting2] = useState(false);

  const load = () => api.get("/wave/settings").then((r) => { setStatus(r.data); setBusinessId(r.data.business_id || ""); });
  useEffect(() => {
    load();
    api.get("/quote-config").then((r) => setFreq(r.data.frequency_multipliers || freq));
    api.get("/business").then((r) => setBiz(r.data));
    api.get("/settings/modules").then((r) => setMods(r.data.visibility || {}));
    api.get("/settings/messaging").then((r) => setMsg(r.data));
    // eslint-disable-next-line
  }, []);

  const saveMsg = async () => {
    try {
      const { data } = await api.put("/settings/messaging", {
        enabled: msg.enabled, from_number: msg.from_number, review_link: msg.review_link,
        msg_on_way: msg.msg_on_way, msg_complete: msg.msg_complete,
      });
      setMsg({ ...data, credentials_configured: msg.credentials_configured });
      toast.success("Texting settings saved");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const sendTest = async () => {
    if (!testTo) return toast.error("Enter a phone number to test");
    setTesting2(true);
    try {
      const { data } = await api.post("/sms/test", { to: testTo });
      if (data.ok) toast.success("Test text sent!");
      else toast.error(data.reason || "Could not send test text");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setTesting2(false); }
  };

  const toggleMod = (key) => setMods((m) => ({ ...m, [key]: m[key] === false }));
  const saveMods = async () => {
    try {
      const { data } = await api.put("/settings/modules", { visibility: mods });
      setMods(data.visibility);
      await refreshModules();
      toast.success("Visibility saved — sidebar and widgets updated");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const saveBiz = async () => {
    try { const { data } = await api.put("/business", biz); setBiz(data); toast.success("Business info saved"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const onLogo = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 800000) return toast.error("Logo must be under 800KB");
    const reader = new FileReader();
    reader.onload = () => setBiz((b) => ({ ...b, logo_url: reader.result }));
    reader.readAsDataURL(file);
  };

  const saveFreq = async () => {
    try {
      const clean = Object.fromEntries(Object.entries(freq).map(([k, v]) => [k, Number(v) || 0]));
      await api.put("/quote-config", { frequency_multipliers: clean });
      toast.success("Quote pricing saved");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const saveSettings = async () => {
    try {
      await api.put("/wave/settings", { token: token || undefined, business_id: businessId });
      toast.success("Wave settings saved");
      setToken("");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const testConnection = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/wave/test-connection");
      setBusinesses(data.businesses || []);
      toast.success(`Connected! Found ${data.businesses.length} business(es).`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setTesting(false); }
  };

  return (
    <div className="space-y-6 max-w-3xl" data-testid="settings-page">
      <Card className="p-6 border-border rounded-lg bg-card" data-testid="business-card">
        <div className="flex items-start gap-4">
          <div className="h-12 w-12 rounded-md bg-primary flex items-center justify-center shrink-0 overflow-hidden">
            {biz.logo_url ? <img src={biz.logo_url} alt="logo" className="h-full w-full object-contain" /> : <Wallet className="h-6 w-6 text-secondary" />}
          </div>
          <div className="flex-1">
            <h2 className="font-display text-lg font-bold tracking-tight">Business Info & Logo</h2>
            <p className="text-sm text-muted-foreground mt-1">Shown on printable quotes and the customer-facing quote page.</p>
          </div>
        </div>
        <div className="mt-6 grid grid-cols-2 gap-4">
          <div className="col-span-2 space-y-1.5"><Label>Business name</Label><Input data-testid="biz-name" value={biz.name} onChange={(e) => setBiz({ ...biz, name: e.target.value })} /></div>
          <div className="col-span-2 space-y-1.5"><Label>Address</Label><Input data-testid="biz-address" value={biz.address} onChange={(e) => setBiz({ ...biz, address: e.target.value })} /></div>
          <div className="space-y-1.5"><Label>Phone</Label><Input data-testid="biz-phone" value={biz.phone} onChange={(e) => setBiz({ ...biz, phone: e.target.value })} /></div>
          <div className="space-y-1.5"><Label>Email</Label><Input data-testid="biz-email" value={biz.email} onChange={(e) => setBiz({ ...biz, email: e.target.value })} /></div>
          <div className="space-y-1.5"><Label>License # (optional)</Label><Input value={biz.license} onChange={(e) => setBiz({ ...biz, license: e.target.value })} /></div>
          <div className="space-y-1.5"><Label>Logo</Label><Input data-testid="biz-logo" type="file" accept="image/*" onChange={onLogo} /></div>
          <div className="col-span-2 space-y-1.5"><Label>Quote terms / footer</Label><Textarea rows={2} value={biz.terms} onChange={(e) => setBiz({ ...biz, terms: e.target.value })} /></div>
        </div>
        <Button data-testid="save-biz-btn" onClick={saveBiz} className="mt-5 bg-primary text-primary-foreground hover:bg-[#152e17] hover:text-white transition-colors duration-200">Save Business Info</Button>
      </Card>

      <Card className="p-6 border-border rounded-lg bg-card" data-testid="module-visibility-card">
        <div className="flex items-start gap-4">
          <div className="h-12 w-12 rounded-md bg-secondary flex items-center justify-center shrink-0">
            <Eye className="h-6 w-6 text-white" />
          </div>
          <div className="flex-1">
            <h2 className="font-display text-lg font-bold tracking-tight">Show / Hide Windows</h2>
            <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
              Turn CRM pages, dashboard widgets, and Crew CAD sections on or off for the whole team. Hidden pages simply disappear from the sidebar. Settings always stays visible.
            </p>
          </div>
        </div>
        {mods && (
          <div className="mt-6 space-y-6">
            {MODULE_GROUPS.map((g) => (
              <div key={g.label}>
                <div className="overline text-muted-foreground mb-3">{g.label}</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                  {g.keys.map(([key, label]) => (
                    <label key={key} className="flex items-center justify-between gap-3 py-1 cursor-pointer">
                      <span className="text-sm font-medium">{label}</span>
                      <Switch
                        data-testid={`mod-toggle-${key}`}
                        checked={mods[key] !== false}
                        onCheckedChange={() => toggleMod(key)}
                      />
                    </label>
                  ))}
                </div>
              </div>
            ))}
            <Button data-testid="save-modules-btn" onClick={saveMods} className="bg-primary text-primary-foreground hover:bg-[#152e17] hover:text-white transition-colors duration-200">Save Visibility</Button>
          </div>
        )}
      </Card>

      <Card className="p-6 border-border rounded-lg bg-card" data-testid="messaging-card">
        <div className="flex items-start gap-4">
          <div className="h-12 w-12 rounded-md bg-primary flex items-center justify-center shrink-0">
            <MessageSquare className="h-6 w-6 text-primary-foreground" />
          </div>
          <div className="flex-1">
            <h2 className="font-display text-lg font-bold tracking-tight">Text Messaging (Twilio)</h2>
            <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
              Send customers an "on my way" text from the Crew CAD and an automatic thank-you + review text when a job is completed.
            </p>
            {msg && (
              <div className="mt-2">
                <Badge variant={msg.credentials_configured ? "default" : "secondary"} className={msg.credentials_configured ? "bg-primary text-primary-foreground" : ""}>
                  {msg.credentials_configured ? "Twilio account connected" : "Twilio account not connected"}
                </Badge>
              </div>
            )}
          </div>
        </div>
        {msg && (
          <div className="mt-6 space-y-4">
            <label className="flex items-center justify-between gap-3 py-1 cursor-pointer">
              <span className="text-sm font-medium">Enable customer texting</span>
              <Switch data-testid="msg-enabled-toggle" checked={msg.enabled !== false} onCheckedChange={(v) => setMsg((m) => ({ ...m, enabled: v }))} />
            </label>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Twilio "From" number</Label>
                <Input data-testid="msg-from-input" value={msg.from_number || ""} onChange={(e) => setMsg((m) => ({ ...m, from_number: e.target.value }))} placeholder="+18645551234" />
                <p className="text-xs text-muted-foreground">The Twilio phone number texts are sent from (E.164 format). Get one at console.twilio.com.</p>
              </div>
              <div className="space-y-1.5">
                <Label>Review link (optional)</Label>
                <Input data-testid="msg-review-input" value={msg.review_link || ""} onChange={(e) => setMsg((m) => ({ ...m, review_link: e.target.value }))} placeholder="https://g.page/r/your-review-link" />
                <p className="text-xs text-muted-foreground">Added to the completion text where you use {"{review}"}.</p>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>"On my way" message</Label>
              <Textarea data-testid="msg-onway-input" rows={2} value={msg.msg_on_way || ""} onChange={(e) => setMsg((m) => ({ ...m, msg_on_way: e.target.value }))} />
            </div>
            <div className="space-y-1.5">
              <Label>Job complete / review message</Label>
              <Textarea data-testid="msg-complete-input" rows={3} value={msg.msg_complete || ""} onChange={(e) => setMsg((m) => ({ ...m, msg_complete: e.target.value }))} />
              <p className="text-xs text-muted-foreground">Use {"{name}"} for the customer's first name and {"{review}"} for the review link.</p>
            </div>
            <div className="flex flex-wrap items-end gap-3">
              <Button data-testid="save-messaging-btn" onClick={saveMsg} className="bg-primary text-primary-foreground hover:bg-[#152e17] hover:text-white transition-colors duration-200">Save Texting Settings</Button>
              <div className="flex items-end gap-2">
                <div className="space-y-1.5">
                  <Label className="text-xs">Send a test text</Label>
                  <Input data-testid="msg-test-input" value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="+18645551234" className="w-44" />
                </div>
                <Button variant="outline" data-testid="msg-test-btn" onClick={sendTest} disabled={testing2} className="border-2">{testing2 ? "Sending…" : "Send Test"}</Button>
              </div>
            </div>
          </div>
        )}
      </Card>

      <Card className="p-6 border-border rounded-lg bg-card">
        <div className="flex items-start gap-4">
          <div className="h-12 w-12 rounded-md bg-primary flex items-center justify-center shrink-0">
            <Wallet className="h-6 w-6 text-secondary" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h2 className="font-display text-lg font-bold tracking-tight">Wave Accounting Sync</h2>
              {status.connected ? (
                <Badge className="bg-primary/10 text-primary border-0 gap-1"><CheckCircle2 className="h-3 w-3" /> Connected</Badge>
              ) : (
                <Badge variant="outline" className="text-muted-foreground">Not connected</Badge>
              )}
            </div>
            <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
              Push invoices and customers straight into Wave for bookkeeping. Requires a Wave
              <strong> Pro or Advisor</strong> plan with a Full-Access API token.
            </p>
          </div>
        </div>

        <div className="mt-6 space-y-4">
          <div className="space-y-1.5">
            <Label>Wave API Token</Label>
            <Input
              data-testid="wave-token-input"
              type="password"
              placeholder={status.has_token ? "•••••••• (saved — enter new to replace)" : "gql_..."}
              value={token}
              onChange={(e) => setToken(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Get it from Wave → Settings → API access (token starts with <code>gql_</code>).
            </p>
          </div>

          <div className="space-y-1.5">
            <Label>Business ID</Label>
            <Input
              data-testid="wave-business-input"
              placeholder="Your Wave Business ID"
              value={businessId}
              onChange={(e) => setBusinessId(e.target.value)}
            />
          </div>

          <div className="flex flex-wrap gap-3">
            <Button data-testid="save-wave-btn" onClick={saveSettings} className="bg-primary text-primary-foreground hover:bg-[#152e17] hover:text-white transition-colors duration-200">
              Save Settings
            </Button>
            <Button variant="outline" data-testid="test-wave-btn" onClick={testConnection} disabled={testing || !status.has_token}>
              <Plug className={`h-4 w-4 mr-1.5 ${testing ? "animate-pulse" : ""}`} /> {testing ? "Testing..." : "Test Connection"}
            </Button>
            <a href="https://developer.waveapps.com/hc/en-us" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm text-accent hover:underline self-center">
              Wave API docs <ExternalLink className="h-3.5 w-3.5" />
            </a>
          </div>

          {businesses.length > 0 && (
            <div className="rounded-md border border-border p-4 bg-muted/40">
              <div className="text-sm font-semibold mb-2">Your Wave businesses:</div>
              <div className="space-y-2">
                {businesses.map((b) => (
                  <div key={b.id} className="flex items-center justify-between text-sm">
                    <span>{b.name}</span>
                    <button onClick={() => { setBusinessId(b.id); toast.success("Business ID filled in"); }} className="text-xs text-accent hover:underline">Use this ID</button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {status.last_sync && (
            <p className="text-xs text-muted-foreground">Last sync: {new Date(status.last_sync).toLocaleString()}</p>
          )}
        </div>
      </Card>

      <Card className="p-6 border-border rounded-lg bg-card" data-testid="quote-config-card">
        <div className="flex items-start gap-4">
          <div className="h-12 w-12 rounded-md bg-secondary flex items-center justify-center shrink-0">
            <Repeat className="h-6 w-6 text-white" />
          </div>
          <div className="flex-1">
            <h2 className="font-display text-lg font-bold tracking-tight">Quote Builder — Frequency Multipliers</h2>
            <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
              Set how each service frequency adjusts the quote total (e.g. discount recurring contracts with a value below 1, or add a premium above 1). Tiered per-service pricing is edited on each item under <strong>Catalog</strong>.
            </p>
          </div>
        </div>
        <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
          {Object.keys(freqLabels).map((f) => (
            <div key={f} className="space-y-1.5">
              <Label>{freqLabels[f]}</Label>
              <div className="flex items-center gap-1">
                <span className="text-muted-foreground text-sm">×</span>
                <Input data-testid={`freq-${f}`} type="number" step="0.05" value={freq[f]} onChange={(e) => setFreq({ ...freq, [f]: e.target.value })} />
              </div>
            </div>
          ))}
        </div>
        <Button data-testid="save-freq-btn" onClick={saveFreq} className="mt-5 bg-primary text-primary-foreground hover:bg-[#152e17] hover:text-white transition-colors duration-200">Save Pricing</Button>
      </Card>

      <Card className="p-6 border-border rounded-lg bg-card">
        <h3 className="font-display font-bold tracking-tight mb-2">Field Mobile App</h3>
        <p className="text-sm text-muted-foreground leading-relaxed">
          This CRM is fully mobile-responsive — your crew can open it on any phone browser to view their schedule,
          update job status, and log completed work. A dedicated native app can be added as a next step.
        </p>
      </Card>
    </div>
  );
}
